import { useState } from 'react'
import { useAppSelector } from '../../../../app/hooks'
import { extractAiData } from '../../../../api/aiDataExtraction/aiDataExtractionApi'
import { AiExtractionRequest, Attachment as AiExtractionAttachment } from '../../../../api/aiDataExtraction/types'
import { useApplyInsertionDataUpdate } from '../../../logic/useApplyInsertionDataUpdate'
import { ProcessingState } from './AiDataExtractionAnalyzingModal'
import { mergeAiExtractionResponseIntoInsertionData } from './aiDataExtractionPrefill'

const SUCCESS_CLOSE_DELAY_MS = 1000
/** Minimum time each progress step stays visible so quick work does not skip labels. */
const MIN_STEP_DISPLAY_MS = 500

const waitUntilMinElapsedSince = (stepStartedAtMs: number, minMs: number) => {
  const elapsed = Date.now() - stepStartedAtMs
  return wait(Math.max(0, minMs - elapsed))
}

interface UseAiExtractionFlowProps {
  getLatestAttachmentsForExtraction: () => AiExtractionRequest['attachments']
  hasSelectedFiles: boolean
  onClearExtractionApiError?: () => void
  onClose: () => void
  onExtractionFailed?: () => void
  onProcessingSuccess?: (attachments: AiExtractionAttachment[]) => void
  onSuccess?: () => void
  waitForPendingUploads: () => Promise<void>
}

export const useAiExtractionFlow = ({
  getLatestAttachmentsForExtraction,
  hasSelectedFiles,
  onClearExtractionApiError,
  onClose,
  onExtractionFailed,
  onProcessingSuccess,
  onSuccess,
  waitForPendingUploads,
}: UseAiExtractionFlowProps) => {
  const applyInsertionDataUpdate = useApplyInsertionDataUpdate()
  const { category, transaction } = useAppSelector((state) => state.flowFeature.flowInformation.metaData)
  const [isAnalyzingOpen, setIsAnalyzingOpen] = useState(false)
  const [processingState, setProcessingState] = useState(ProcessingState.Preparing)

  const startAnalyzing = async () => {
    if (!hasSelectedFiles) {
      return
    }

    onClearExtractionApiError?.()
    setIsAnalyzingOpen(true)
    onClose()
    setProcessingState(ProcessingState.Preparing)
    const preparingShownAt = Date.now()

    try {
      await waitForPendingUploads()
      await waitUntilMinElapsedSince(preparingShownAt, MIN_STEP_DISPLAY_MS)

      setProcessingState(ProcessingState.Analyzing)
      const analyzingShownAt = Date.now()

      const attachments = getLatestAttachmentsForExtraction()
      if (attachments.length === 0) {
        throw new Error('No uploaded attachments available for ai extraction')
      }

      const extractionResponse = await extractAiData({
        attachments,
        realEstateCategory: category,
        transaction,
      })

      await waitUntilMinElapsedSince(analyzingShownAt, MIN_STEP_DISPLAY_MS)

      setProcessingState(ProcessingState.Transferring)
      const transferringShownAt = Date.now()
      applyInsertionDataUpdate((currentInsertionData) =>
        mergeAiExtractionResponseIntoInsertionData(currentInsertionData, extractionResponse)
      )
      await waitUntilMinElapsedSince(transferringShownAt, MIN_STEP_DISPLAY_MS)

      setProcessingState(ProcessingState.Completing)
      const completingShownAt = Date.now()
      await waitUntilMinElapsedSince(completingShownAt, MIN_STEP_DISPLAY_MS)

      setProcessingState(ProcessingState.Done)
      await wait(SUCCESS_CLOSE_DELAY_MS)
      setIsAnalyzingOpen(false)
      onProcessingSuccess?.(attachments)
      onSuccess?.()
    } catch {
      setProcessingState(ProcessingState.Error)
      if (getLatestAttachmentsForExtraction().length === 0) {
        setIsAnalyzingOpen(false)
        onExtractionFailed?.()
      }
    }
  }

  return {
    isAnalyzingOpen,
    processingState,
    retryAnalyzing: () => {
      void startAnalyzing()
    },
    startAnalyzing: () => {
      void startAnalyzing()
    },
  }
}

const wait = (timeoutMs: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, timeoutMs)
  })
